import { EGDSSpacing } from '@egds/react-core/spacing';
import { inject, observer } from 'mobx-react';
import { Level, liveAnnounce } from '@egds/react-core/utils';
import { useHistory, useLocation } from 'react-router-dom';
import * as React from 'react';
import { ErrorBoundary } from 'bernie-view';
import { mergeQueryString, updateSearch } from 'bernie-core';
import { useLocalization } from '@shared-ui/localization-context';
import { PropertyActivitiesCrossSellContextProvider } from '@shared-ui/retail-lodging-property-activities-cross-sell';
import { PropertyOffers } from '@shared-ui/lodging-property-offers';
import { RefTarget } from '@shared-ui/ref-context';
import { ViewLarge, Viewport, ViewSmall } from '@shared-ui/viewport-context';
import { InfositeQueryAdapter } from 'stores/adapters/infosite-query-adapter';
import { rewriteHistory } from 'utils/route-store-utils';
import { singleUseQueryParams } from 'stores/models';
import { SearchQueryAdapter } from 'stores/adapters/search-query-adapter';
import { multiItemCompleteSearchCriteria } from 'components/multi-item/utils';
import { FocusTarget } from 'components/common/focus/focus-target';
import { gql, useFragment } from '@apollo/client';
import { PropertyActivitiesCrossSell } from 'components/infosite/template-components/property-activities-cross-sell-wrapper';
import { ReferrerContextProvider } from '../shared-ui/form/infosite-referrer-context';
import { SearchWizardComposer } from './search-wizard-composer';
import { SharedUiBookByPhoneWrapper } from 'components/infosite/shared-ui/details/book-by-phone-wrapper';
import { SharedUiLodgingOneKeyBurnSwitchWrapper } from '../shared-ui/offers/lodging-one-key-burn-switch-wrapper';
import { SharedUiLoyaltyDiscountToggleWrapper } from '../shared-ui/offers/loyalty-discount-toggle-wrapper';
import { SharedUiOffersWrapper } from 'components/infosite/shared-ui/offers/offers-wrapper';
import { SharedUiPriceInsightWrapper } from '../shared-ui/offers/price-insight-wrapper';
import { SharedUiTravelersLinkWrapper } from 'components/infosite/shared-ui/form/field-wrappers';
import { StickyBookBar } from 'components/infosite/template-components/sticky-book-bar';
export const CACHED_PROPERTY_OFFERS_A11Y_LABEL_FRAGMENT = gql`
  fragment LoadingA11yLabel on OfferDetails {
    loading {
      accessibilityLabel
    }
  }
`;
export const RoomsAndRates = inject('currentHotel', 'context', 'multiItem', 'searchResults', 'experiment')(observer(({
  currentHotel,
  context,
  multiItem,
  searchResults
}) => {
  var _a, _b;
  const l10n = useLocalization();
  const history = useHistory();
  const location = useLocation();
  const {
    data: propertyOffers
  } = useFragment({
    fragment: CACHED_PROPERTY_OFFERS_A11Y_LABEL_FRAGMENT,
    from: {
      __typename: 'OfferDetails',
      id: currentHotel === null || currentHotel === void 0 ? void 0 : currentHotel.hotelId
    }
  });
  const accessibilityLabel = (_b = (_a = propertyOffers.loading) === null || _a === void 0 ? void 0 : _a.accessibilityLabel) !== null && _b !== void 0 ? _b : '';
  const onUpdateAvailabilityTravelers = fields => {
    currentHotel === null || currentHotel === void 0 ? void 0 : currentHotel.setOfferData(fields);
    onUpdateResultsAvailabilityWizard(undefined, currentHotel === null || currentHotel === void 0 ? void 0 : currentHotel.offerSearchData, true);
  };
  const onUpdateResultsAvailabilityWizard = (event, offerSearchData, goBack = true) => {
    handleUpdateResults({
      accessibilityLabel,
      context,
      searchResults,
      l10n,
      history,
      location,
      event,
      criteria: Object.assign(Object.assign({}, currentHotel === null || currentHotel === void 0 ? void 0 : currentHotel.searchCriteria.toJSON(multiItemCompleteSearchCriteria)), SearchQueryAdapter.parseIn(offerSearchData)),
      goBack
    });
  };
  const onUpdateResults = (event, searchCriteria, goBack = true) => {
    handleUpdateResults({
      accessibilityLabel,
      context,
      searchResults,
      l10n,
      history,
      location,
      event,
      criteria: searchCriteria.toJSON(multiItemCompleteSearchCriteria),
      goBack,
      queryCombiner: (prevQuery, newQuery) => newQuery
    });
  };
  React.useEffect(() => {
    const {
      startDate,
      endDate,
      adults,
      children
    } = SearchQueryAdapter.parseOut(currentHotel === null || currentHotel === void 0 ? void 0 : currentHotel.searchCriteria);
    // 0 - children from search is undefined
    const childrenFormatted = typeof children === 'undefined' ? '' : children;
    currentHotel === null || currentHotel === void 0 ? void 0 : currentHotel.setOfferData({
      startDate,
      endDate,
      adults,
      children: childrenFormatted
    });
  }, [currentHotel]);
  const isMultiItem = multiItem && multiItem.isPackageShoppingPath();
  const searchWizardComposerProps = {
    currentHotel,
    isMultiItem,
    onUpdateResults,
    onUpdateResultsAvailabilityWizard
  };
  const roomsAndRatesComponent = React.createElement(ReferrerContextProvider, null, React.createElement(EGDSSpacing, {
    padding: {
      blockend: 'three'
    }
  }, React.createElement(SharedUiBookByPhoneWrapper, null)), React.createElement(PropertyActivitiesCrossSellContextProvider, null, React.createElement(SharedUiOffersWrapper, null, React.createElement(PropertyOffers.HeaderRight, null, !isMultiItem && React.createElement(Viewport, null, React.createElement(ViewSmall, null, React.createElement(SharedUiTravelersLinkWrapper, {
    handleChange: onUpdateAvailabilityTravelers
  })), React.createElement(ViewLarge, null))), React.createElement(PropertyOffers.Form, null, React.createElement("div", null, React.createElement(SearchWizardComposer, Object.assign({}, searchWizardComposerProps)), React.createElement(SharedUiPriceInsightWrapper, null), React.createElement(SharedUiLoyaltyDiscountToggleWrapper, null), React.createElement(SharedUiLodgingOneKeyBurnSwitchWrapper, {
    showBorder: false
  })))), React.createElement(PropertyActivitiesCrossSell, {
    skipSsr: true
  })), React.createElement(Viewport, null, React.createElement(ViewSmall, null, React.createElement("div", {
    "data-stid": "sticky-book-bar"
  }, React.createElement(StickyBookBar, null))), React.createElement(ViewLarge, null, React.createElement(React.Fragment, null))));
  return React.createElement(ErrorBoundary, {
    label: "hotel.information.rooms.roomsAndRates"
  }, React.createElement(FocusTarget, {
    targetKey: "searchForm"
  }, React.createElement(EGDSSpacing, {
    margin: {
      blockstart: 'six'
    }
  }, React.createElement("div", {
    "data-stid": "rooms-rates"
  }, React.createElement(RefTarget, {
    name: "Offers",
    tabIndex: -1
  }, roomsAndRatesComponent)))));
}));
export const handleUpdateResults = ({
  accessibilityLabel,
  context,
  searchResults,
  l10n,
  history,
  location,
  event,
  criteria,
  goBack = true,
  queryCombiner
}) => {
  if (event) {
    event.preventDefault();
  }
  const keysToKeepFromOriginalQuery = key => !key.startsWith('pwaDialog') && !key.startsWith('rm') && !singleUseQueryParams.includes(key);
  const params = Object.assign(Object.assign({}, criteria), {
    localDateFormat: context === null || context === void 0 ? void 0 : context.localDateFormat
  });
  const queryString = mergeQueryString(location.search, InfositeQueryAdapter.parseOut(params, l10n), keysToKeepFromOriginalQuery);
  searchResults === null || searchResults === void 0 ? void 0 : searchResults.clearDefaultRequestFields();
  if (goBack) {
    rewriteHistory(history, Object.assign(Object.assign({}, location), {
      search: queryString
    }));
  } else {
    liveAnnounce(accessibilityLabel, Level.POLITE);
    updateSearch({
      history,
      location,
      newParams: queryString,
      replace: true,
      queryCombiner
    });
  }
};